import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Select,
  Badge,
  Alert,
  AlertIcon,
  Progress,
  Icon,
  Spinner,
  Center,
  useToast,
} from '@chakra-ui/react';
import { FiZap, FiCheckCircle } from 'react-icons/fi';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../utils/supabase';
import { fetchUserCampaigns, enrollContactInCampaign } from '../../../api/supabase/automations';
import { fetchAccessibleOwnerIds } from '../../../utils/recordVisibility';

/**
 * Bulk campaign enrollment — the capability that exists nowhere else in the
 * app (research confirmed: enrollment is per-contact or event-triggered only).
 *
 * Deliberately thin: one pre-check query finds contacts already actively
 * enrolled, then the battle-tested single-contact enrollContactInCampaign()
 * runs for each remaining contact (small concurrency pool). No new write
 * logic — each enrollment is byte-for-byte what the ContactCampaignPanel
 * "Enroll" button does.
 */

const ENROLL_CAP = 500;
const CONCURRENCY = 4;
const PRECHECK_CHUNK = 200;

export default function BulkEnrollCampaignModal({ isOpen, onClose, selectedContacts = [], onSuccess }) {
  const { currentUser, SuperAdmin, Roles } = useAuth();
  const isCRMAdmin = SuperAdmin || Roles?.includes('CRM Admin');
  const toast = useToast();

  const [campaigns, setCampaigns] = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [selectedCampaignId, setSelectedCampaignId] = useState('');
  const [phase, setPhase] = useState('pick'); // pick | running | done
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [result, setResult] = useState({ enrolled: 0, skipped: 0, failed: 0, errors: [] });

  const overCap = selectedContacts.length > ENROLL_CAP;

  // Load the current user's campaigns fresh on every open
  useEffect(() => {
    if (!isOpen || !currentUser?.uid) return;
    setPhase('pick');
    setSelectedCampaignId('');
    setResult({ enrolled: 0, skipped: 0, failed: 0, errors: [] });
    setCampaignsLoading(true);
    fetchUserCampaigns(currentUser.uid)
      .then((rows) => {
        const enabled = (rows || []).filter((c) => c.is_enabled);
        setCampaigns(enabled);
      })
      .catch((err) => {
        console.error('[BulkEnroll] Failed to load campaigns:', err);
        toast({ title: 'Error loading campaigns', description: err.message, status: 'error', duration: 4000 });
        setCampaigns([]);
      })
      .finally(() => setCampaignsLoading(false));
  }, [isOpen, currentUser?.uid, toast]);

  const campaignName = (c) => c?.template?.name || 'Untitled campaign';

  const handleEnroll = async () => {
    if (!selectedCampaignId || overCap || !selectedContacts.length) return;

    setPhase('running');
    const ids = [...selectedContacts];

    try {
      // One pre-check pass: who is already actively enrolled?
      const alreadyEnrolled = new Set();
      for (let i = 0; i < ids.length; i += PRECHECK_CHUNK) {
        const chunk = ids.slice(i, i + PRECHECK_CHUNK);
        const { data, error } = await supabase
          .from('crm_automation_queue')
          .select('contact_id')
          .eq('user_campaign_id', selectedCampaignId)
          .in('contact_id', chunk)
          .in('status', ['pending', 'processing']);
        if (error) throw error;
        (data || []).forEach((row) => alreadyEnrolled.add(row.contact_id));
      }

      const toEnroll = ids.filter((id) => !alreadyEnrolled.has(id));
      const tally = { enrolled: 0, skipped: alreadyEnrolled.size, failed: 0, errors: [] };
      setProgress({ done: 0, total: toEnroll.length });

      // Visibility guard (Phase 4): resolve accessible owners ONCE for the
      // whole batch; each enrollment then verifies the viewer can see the
      // contact without an extra RPC per contact.
      const viewer = {
        uid: currentUser?.uid,
        isAdmin: !!isCRMAdmin,
        accessibleOwnerIds: isCRMAdmin || !currentUser?.uid
          ? undefined
          : await fetchAccessibleOwnerIds(currentUser.uid, 'contact'),
      };

      // Small worker pool over the existing single-contact save path
      let cursor = 0;
      const worker = async () => {
        while (cursor < toEnroll.length) {
          const myIndex = cursor;
          cursor += 1;
          const contactId = toEnroll[myIndex];
          try {
            await enrollContactInCampaign(selectedCampaignId, contactId, viewer);
            tally.enrolled += 1;
          } catch (err) {
            if ((err.message || '').includes('already actively enrolled')) {
              tally.skipped += 1;
            } else {
              tally.failed += 1;
              if (tally.errors.length < 3) tally.errors.push(err.message);
            }
          }
          setProgress({ done: myIndex + 1, total: toEnroll.length });
        }
      };
      await Promise.all(Array.from({ length: CONCURRENCY }, worker));

      setResult(tally);
      setPhase('done');
      if (tally.enrolled > 0 && onSuccess) onSuccess();
    } catch (err) {
      console.error('[BulkEnroll] Enrollment failed:', err);
      toast({ title: 'Bulk enrollment failed', description: err.message, status: 'error', duration: 5000 });
      setPhase('pick');
    }
  };

  const handleClose = () => {
    if (phase === 'running') return; // don't abandon a run mid-flight
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md" closeOnOverlayClick={phase !== 'running'}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack spacing={2}>
            <Icon as={FiZap} color="purple.400" />
            <Text>Enroll in Campaign</Text>
          </HStack>
        </ModalHeader>
        {phase !== 'running' && <ModalCloseButton />}

        <ModalBody>
          {phase === 'pick' && (
            <VStack align="stretch" spacing={4}>
              <HStack>
                <Badge colorScheme="purple" fontSize="sm" px={2} py={0.5}>
                  {selectedContacts.length.toLocaleString()} contact{selectedContacts.length === 1 ? '' : 's'} selected
                </Badge>
              </HStack>

              {overCap && (
                <Alert status="warning" borderRadius="md" fontSize="sm">
                  <AlertIcon />
                  Bulk enrollment is capped at {ENROLL_CAP.toLocaleString()} contacts per run. Narrow your
                  filters and try again.
                </Alert>
              )}

              {campaignsLoading ? (
                <Center py={4}><Spinner size="sm" color="purple.400" /></Center>
              ) : campaigns.length === 0 ? (
                <Alert status="info" borderRadius="md" fontSize="sm">
                  <AlertIcon />
                  You have no enabled campaigns. Enable one under My Marketing first.
                </Alert>
              ) : (
                <Select
                  placeholder="Choose one of your campaigns…"
                  value={selectedCampaignId}
                  onChange={(e) => setSelectedCampaignId(e.target.value)}
                >
                  {campaigns.map((c) => (
                    <option key={c.id} value={c.id}>{campaignName(c)}</option>
                  ))}
                </Select>
              )}

              <Text fontSize="xs" color="gray.500">
                Contacts already actively enrolled in this campaign are skipped automatically. Each
                enrollment starts the campaign from its first step — exactly like enrolling from the
                contact's campaign panel.
              </Text>
            </VStack>
          )}

          {phase === 'running' && (
            <VStack align="stretch" spacing={3} py={2}>
              <Text fontSize="sm">
                Enrolling {progress.total.toLocaleString()} contact{progress.total === 1 ? '' : 's'}…
              </Text>
              <Progress
                value={progress.total ? (progress.done / progress.total) * 100 : 100}
                size="sm"
                colorScheme="purple"
                borderRadius="md"
                hasStripe
                isAnimated
              />
              <Text fontSize="xs" color="gray.500">
                {progress.done.toLocaleString()} of {progress.total.toLocaleString()} processed — keep this window open
              </Text>
            </VStack>
          )}

          {phase === 'done' && (
            <VStack align="stretch" spacing={3} py={2}>
              <HStack spacing={2}>
                <Icon as={FiCheckCircle} color="green.400" boxSize={5} />
                <Text fontWeight="semibold">Enrollment complete</Text>
              </HStack>
              <HStack spacing={2} flexWrap="wrap">
                <Badge colorScheme="green">{result.enrolled.toLocaleString()} enrolled</Badge>
                {result.skipped > 0 && (
                  <Badge colorScheme="yellow">{result.skipped.toLocaleString()} already enrolled (skipped)</Badge>
                )}
                {result.failed > 0 && <Badge colorScheme="red">{result.failed.toLocaleString()} failed</Badge>}
              </HStack>
              {result.errors.length > 0 && (
                <Alert status="error" borderRadius="md" fontSize="xs">
                  <AlertIcon />
                  <VStack align="start" spacing={0.5}>
                    {result.errors.map((e, i) => <Text key={i}>{e}</Text>)}
                  </VStack>
                </Alert>
              )}
            </VStack>
          )}
        </ModalBody>

        <ModalFooter>
          {phase === 'pick' && (
            <>
              <Button variant="ghost" mr={3} onClick={handleClose}>Cancel</Button>
              <Button
                colorScheme="purple"
                leftIcon={<FiZap />}
                onClick={handleEnroll}
                isDisabled={!selectedCampaignId || overCap || selectedContacts.length === 0}
              >
                Enroll {selectedContacts.length.toLocaleString()}
              </Button>
            </>
          )}
          {phase === 'done' && (
            <Button colorScheme="purple" onClick={handleClose}>Done</Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
